// src/lib/errorMessages.js
//
// Hardening Sprint Track 5.2 — translate Supabase / Postgres error
// payloads into user-friendly strings.
//
// Supabase returns raw Postgres errors for RLS violations, constraint
// failures, and foreign-key blowups. Surfacing them verbatim either
// leaks schema details ("row-level security policy violation on
// event_players for INSERT") or buries the actionable cause inside a
// long technical string. This module is the single place that maps a
// Postgres error to a one-line message the user can act on.
//
// Returns a string OR null when the error is null/undefined (so callers
// can do `setMessage(formatSupabaseError(error))` without a null check).

// Postgres SQLSTATE codes we care about. Supabase surfaces these as
// error.code on the PostgrestError object.
const PG_CODE = {
  UNIQUE_VIOLATION:        '23505',
  FOREIGN_KEY_VIOLATION:   '23503',
  NOT_NULL_VIOLATION:      '23502',
  CHECK_VIOLATION:         '23514',
  EXCLUSION_VIOLATION:     '23P01',
  // 42501 = insufficient_privilege. RLS violations come back as this OR
  // a custom Postgres-side RAISE EXCEPTION; cover both.
  INSUFFICIENT_PRIVILEGE:  '42501',
  // 42P17 = invalid_object_definition. Hit this when an RLS policy
  // self-references the same table — almost always a backend bug, not
  // user-actionable, so we surface it as "Internal config error".
  INVALID_OBJECT_DEFINITION: '42P17',
}

export function formatSupabaseError(error) {
  if (!error) return null

  // Network / fetch failure — supabase wraps these without a Postgres
  // code, just a JS-level message. Detect by message rather than code.
  const msg = error.message || ''
  if (/failed to fetch|networkerror|err_connection/i.test(msg)) {
    return 'Network error. Check your connection and try again.'
  }

  switch (error.code) {
    case PG_CODE.UNIQUE_VIOLATION:
      return 'That already exists. Try a different value.'
    case PG_CODE.FOREIGN_KEY_VIOLATION:
      return 'Referenced item is missing or was deleted. Refresh and try again.'
    case PG_CODE.NOT_NULL_VIOLATION:
      return 'A required field is missing.'
    case PG_CODE.CHECK_VIOLATION:
      return 'One of the values is not allowed. Check the form.'
    case PG_CODE.EXCLUSION_VIOLATION:
      return 'This conflicts with an existing entry.'
    case PG_CODE.INSUFFICIENT_PRIVILEGE:
      return "You don't have permission to do this. Check your role."
    case PG_CODE.INVALID_OBJECT_DEFINITION:
      return 'Internal configuration error. Contact your GM.'
    default:
      break
  }

  // Catch-all for RLS messages that come back without a code (raised
  // via RAISE EXCEPTION instead of a constraint failure).
  if (/row-level security|violates.*policy|new row violates row-level security/i.test(msg)) {
    return "You don't have permission to do this. Check your role."
  }

  // Trim leading "duplicate key value violates unique constraint ..." /
  // similar technical prefixes if any leaked through.
  const trimmed = msg.replace(/^[A-Z_]+: /i, '').trim()
  return trimmed || 'Something went wrong. Try again.'
}

export default formatSupabaseError
